import { useMemo } from 'react';
import { create } from 'zustand';
import { StoreApi, UseBoundStore, useStore } from 'zustand';

import { commands } from '../commands';
import { registerCommands } from '../../../stores/commandRegistryStore';
import { useCommandRegistryStore } from '../../../stores/commandRegistryStore';
import { entityService } from '../../../services/entityService';
import { PDFDocument } from '../lib/pdfium-wasm/types';
import { HighlightEntity, HighlightColor } from '../../../types/entities';
// entityCRDTService removed — entity-system Y.Doc handles sync via REST broadcast
import fetchService from '../../../services/fetchService';
import { API_BASE_URL } from '../../../api/config';

import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('PDFIUMSTORE_APP', {enabled: false});

// Define page contents interface
export interface PageContents {
  [pageNumber: string]: string;
}

// Define the state interface
export interface PDFiumAppState {
  windowId: string;
  spaceId: string;
  status: 'idle' | 'loading' | 'error';
  error: Error | null;
  initialized: boolean;
  isInitializing: boolean;
  isInitialized: boolean;
  entityId: string | null;
  // The full PDF entity data
  entity: any | null;
  // PDF content and enrichments
  rawText: string;
  pageContents: PageContents;
  pageSummaries: Record<string, string>;
  pdfDoc: PDFDocument | null;
  hasEnrichments: boolean;
  enrichmentError: string | null;
  enrichmentStatus: 'not_started' | 'processing' | 'completed' | 'completed_partial' | 'completed_no_search' | 'failed';
  // Viewer state
  scale: number;
  selection: any | null;
  // Highlights
  highlights: Map<string, HighlightEntity>;
}

// Create highlight params interface
export interface CreateHighlightParams {
  pageIndex: number;
  startIndex: number;
  endIndex: number;
  color: HighlightColor;
  text: string;
  comment?: string;
}

// Mutation options interface
interface MutationOpts {
  log?: boolean;
  source?: 'local' | 'crdt' | 'event';
  syncCRDT?: boolean;
}

// Define the store interface
export interface PDFiumStore extends PDFiumAppState {
  initialize: (entityId: string, force?: boolean) => Promise<void>;
  updateEnrichmentData: (enrichmentUpdate: any) => void;
  cleanup: () => void;
  setScale: (scale: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  setSelection: (selection: any | null) => void;
  setPDFDoc: (doc: PDFDocument | null) => void;
  setState: (state: Partial<PDFiumAppState>) => void;
  getPageText: (pageNumber: number) => string | null;
  getAllPageSummaries: () => Record<string, string>;
  getPageSummary: (pageNumber: number) => string | null;
  
  // Internal highlight helpers (single mutation funnel)
  _applyLocalHighlightUpsert: (highlight: HighlightEntity, opts?: MutationOpts) => void;
  _applyLocalHighlightDelete: (highlightId: string, opts?: MutationOpts) => void;
  
  // Highlight actions (business logic)
  actions: {
    createHighlight: (params: CreateHighlightParams) => Promise<HighlightEntity>;
    updateHighlight: (id: string, updates: Partial<HighlightEntity>) => Promise<void>;
    deleteHighlight: (id: string) => Promise<void>;
    loadHighlights: (pdfId: string) => Promise<void>;
  };
  
  // Highlight getters
  getHighlightsForPage: (pageIndex: number) => HighlightEntity[];
  getAllHighlights: () => HighlightEntity[];
}

// Define the store hook type
export type PDFiumStoreHook = UseBoundStore<StoreApi<PDFiumStore>>;

// Create the store with proper typing
export const createPDFiumStore = (windowId: string, spaceId: string) => {
  // Create a set of subscribers


  // Create the base store
  return create<PDFiumStore>((set, get) => ({
    // Initial state
    windowId,
    spaceId,
    status: 'idle',
    error: null,
    initialized: false,
    isInitializing: false,
    isInitialized: false,
    entityId: null,
    entity: null,
    rawText: '',
    pageContents: {},
    pageSummaries: {},
    pdfDoc: null,
    hasEnrichments: false,
    enrichmentError: null,
    enrichmentStatus: 'not_started',
    scale: 1.0,
    selection: null,
    highlights: new Map(),

    // Set partial state
    setState: (newState: Partial<PDFiumAppState>) => {
      set((state) => ({
        ...state,
        ...newState
      }));
    },
    
    // Fast update for enrichment status changes (no full reload needed)
    updateEnrichmentData: (enrichmentUpdate: any) => {
      log.log('[PDFiumStore] 📊 Fast update - enrichment data changed:', {
        newStatus: enrichmentUpdate.skeleton?.enrichmentStatus,
        hasMetadata: !!enrichmentUpdate.skeleton?.referenceMetadata,
        hasEnrichments: !!enrichmentUpdate.skeleton?.enrichments
      });
      
      set((state) => ({
        ...state,
        entity: enrichmentUpdate,  // Update full entity
        enrichmentStatus: enrichmentUpdate.skeleton?.enrichmentStatus || state.enrichmentStatus,
        hasEnrichments: !!enrichmentUpdate.skeleton?.enrichments || !!enrichmentUpdate.skeleton?.enrichedData,
        enrichmentError: enrichmentUpdate.skeleton?.enrichmentError || null
      }));
      
      log.log('[PDFiumStore] ✅ Enrichment fields updated without full reload');
    },

    // Initialize function - loads the full PDF entity and its enrichments
    initialize: async (entityId: string, force: boolean = false) => {
      log.log('[PDFiumStore] Starting initialization for entity:', { entityId, windowId, force });
      
      const state = get();
      
      if (state.initialized && state.entityId === entityId && state.entity && !force) {
        log.log('[PDFiumStore] Already fully initialized, skipping (use force=true to reload)');
        set({ isInitialized: true });
        return;
      }
      
      if (force) {
        log.log('[PDFiumStore] 🔄 Force refresh - reloading entity data');
      }
      
      if (state.status === 'loading' && state.entityId === entityId) {
        log.log('[PDFiumStore] Already loading, waiting...');
        return;
      }

      try {
        set({ 
          status: 'loading', 
          entityId,
          isInitializing: true,
          isInitialized: false
        });

        // For PDF files, try to get enriched data using FileService which has access to the correct endpoints
        let entity;
        let pdfEnrichments = null;
        
        try {
          // First load the basic entity
          entity = await entityService.get(entityId);
          log.log('[PDFiumStore] Loaded basic entity:', entity);
          
          // If it's a PDF and doesn't have enrichments, try to load them separately
          if (!entity?.skeleton?.enrichments) {
            log.log('[PDFiumStore] No enrichments in entity, trying to load PDF enrichment data...');
            // Import FileService here to avoid circular dependencies
            const { FileService } = await import('../../../services/FileService');
            const fileService = new FileService();
            
            try {
              pdfEnrichments = await fileService.loadPdfEnrichment(entityId);
              log.log('[PDFiumStore] Loaded PDF enrichments separately:', pdfEnrichments);
              
              // Update the entity with enrichments
              if (pdfEnrichments) {
                entity = {
                  ...entity,
                  skeleton: {
                    ...entity.skeleton,
                    enrichments: pdfEnrichments
                  }
                };
              }
            } catch (enrichError: any) {
              // 400 errors are expected for PDFs that haven't been enriched yet
              if (enrichError?.message?.includes('400') || enrichError?.message?.includes('PDF has not been enriched yet')) {
                log.log('[PDFiumStore] PDF not enriched yet (expected for new PDFs)');
              } else {
                log.log('[PDFiumStore] Failed to load PDF enrichments:', enrichError);
              }
            }
          }
        } catch (error) {
          log.error('[PDFiumStore] Failed to load entity:', error);
          throw error;
        }
        
        // Extract enrichments
        const enrichments = entity?.skeleton?.enrichments;
        const hasEnrichments = !!enrichments;
        let rawText = '';
        const pageContents: PageContents = {};
        const pageSummaries: Record<string, string> = {};
        const enrichmentError = entity?.skeleton?.enrichment_error || null;
        const enrichmentStatus: PDFiumAppState['enrichmentStatus'] = entity?.skeleton?.enrichmentStatus || 'not_started';
        
        // Extract raw text if available
        if (enrichments?.raw_text) {
          rawText = enrichments.raw_text;
        }
        
        // Extract page contents from sections if available
        if (enrichments?.sections) {
          Object.entries(enrichments.sections).forEach(([key, value]) => {
            // Extract page number from "Page X" format
            const pageMatch = key.match(/Page (\d+)/);
            if (pageMatch && pageMatch[1]) {
              const pageNumber = parseInt(pageMatch[1], 10);
              if (!isNaN(pageNumber)) {
                // Handle both new format (string) and legacy format (object with text field)
                if (typeof value === 'string') {
                  pageContents[pageNumber.toString()] = value;
                } else if (typeof value === 'object' && value && 'text' in value) {
                  pageContents[pageNumber.toString()] = (value as any).text;
                } else {
                  pageContents[pageNumber.toString()] = String(value || '');
                }
              }
            }
          });
        }
        
        // Extract page summaries - now check for the new summaries field first
        if (enrichments?.summaries && typeof enrichments.summaries === 'object' && !Array.isArray(enrichments.summaries)) {
          log.log('[PDFiumStore] Found summaries field in enrichments');
          // Process each page summary from the dedicated summaries field
          Object.entries(enrichments.summaries).forEach(([key, value]) => {
            // Extract page number from "Page X" format or use key directly
            const pageMatch = key.match(/Page (\d+)/);
            if (pageMatch && pageMatch[1]) {
              const pageNumber = parseInt(pageMatch[1], 10);
              if (!isNaN(pageNumber)) {
                pageSummaries[pageNumber.toString()] = typeof value === 'string' ? value : JSON.stringify(value);
              }
            } else if (/^\d+$/.test(key)) {
              // If key is already a number string, use it directly
              pageSummaries[key] = typeof value === 'string' ? value : JSON.stringify(value);
            }
          });
        }
        // Fallback: Extract summaries from sections (legacy format)
        else if (enrichments?.sections && Object.keys(pageSummaries).length === 0) {
          log.log('[PDFiumStore] Extracting summaries from sections (legacy format)');
          Object.entries(enrichments.sections).forEach(([key, value]) => {
            const pageMatch = key.match(/Page (\d+)/);
            if (pageMatch && pageMatch[1]) {
              const pageNumber = parseInt(pageMatch[1], 10);
              if (!isNaN(pageNumber)) {
                // Extract summary from section object if it exists
                if (typeof value === 'object' && value && 'summary' in value) {
                  const summary = (value as any).summary;
                  if (summary) {
                    pageSummaries[pageNumber.toString()] = typeof summary === 'string' ? summary : JSON.stringify(summary);
                  }
                }
              }
            }
          });
        }
        // Check if pageSummaries exists in enrichments (alternative field name)
        else if ((enrichments as any)?.pageSummaries && Object.keys(pageSummaries).length === 0) {
          log.log('[PDFiumStore] Found pageSummaries field');
          const pageSummariesData = (enrichments as any).pageSummaries;
          // If it's an array, convert to object with page numbers as keys
          if (Array.isArray(pageSummariesData)) {
            pageSummariesData.forEach((summary: any, index: number) => {
              const pageNumber = (index + 1).toString();
              if (typeof summary === 'string') {
                pageSummaries[pageNumber] = summary;
              } else if (summary && typeof summary === 'object') {
                try {
                  pageSummaries[pageNumber] = JSON.stringify(summary);
                } catch (e) {
                  pageSummaries[pageNumber] = `Summary for page ${pageNumber}`;
                }
              }
            });
          } 
          // If it's already an object mapping page numbers to summaries
          else if (typeof pageSummariesData === 'object') {
            Object.entries(pageSummariesData).forEach(([key, value]) => {
              pageSummaries[key] = typeof value === 'string' ? value : JSON.stringify(value);
            });
          }
        }
        // Sometimes it might be an array under 'summaries' (old format?)
        else if (enrichments?.summaries && Array.isArray(enrichments.summaries)) {
          log.log('[PDFiumStore] Found summaries in array format');
          enrichments.summaries.forEach((summary: any, index: number) => {
            const pageNumber = (index + 1).toString();
            if (typeof summary === 'string') {
              pageSummaries[pageNumber] = summary;
            } else if (summary && typeof summary === 'object') {
              try {
                pageSummaries[pageNumber] = JSON.stringify(summary);
              } catch (e) {
                pageSummaries[pageNumber] = `Summary for page ${pageNumber}`;
              }
            }
          });
        }
        
        log.log('[PDFiumStore] Processed enrichments:', {
          pageContentCount: Object.keys(pageContents).length,
          pageSummaryCount: Object.keys(pageSummaries).length,
          summaryKeys: Object.keys(pageSummaries).slice(0, 3),
          hasRawText: !!rawText,
          enrichmentError
        });
        
        // Update state with all the extracted data
        set({
          status: 'idle',
          entity,
          rawText,
          pageContents,
          pageSummaries,
          hasEnrichments,
          enrichmentError,
          enrichmentStatus,
          initialized: true,
          isInitializing: false,
          isInitialized: true
        });

        // Load highlights for this PDF
        try {
          await get().actions.loadHighlights(entityId);
        } catch (error) {
          log.error('[PDFiumStore] Failed to load highlights:', error);
        }

        // Note: CRDT sync for highlights happens via entityStore's CRDT observer
        // Highlights are regular entities, so they sync automatically
      } catch (error) {
        log.error('[PDFiumStore] Error initializing:', error);
        set({ 
          status: 'error', 
          error: error as Error,
          isInitializing: false,
          isInitialized: false
        });
      }
    },

    // Cleanup function
    cleanup: () => {
      const { pdfDoc } = get();
      
      // Clean up PDF document if it exists
      if (pdfDoc) {
        try {
          log.log('[PDFiumStore] Cleaning up PDF document');
          pdfDoc.destroy();
        } catch (error) {
          log.error('[PDFiumStore] Error destroying PDF document:', error);
        }
      }
      
      set({
        pdfDoc: null,
        entity: null,
        initialized: false,
        isInitialized: false
      });
    },

    // Zoom controls
    setScale: (scale: number) => {
      set({ scale });
    },
    
    zoomIn: () => {
      set(state => ({ scale: state.scale * 1.1 }));
    },
    
    zoomOut: () => {
      set(state => ({ scale: state.scale / 1.1 }));
    },
    
    resetZoom: () => {
      set({ scale: 1.0 });
    },

    // Selection handling
    setSelection: (selection: any | null) => {
      set({ selection });
    },

    // Set PDF document
    setPDFDoc: (doc: PDFDocument | null) => {
      set({ pdfDoc: doc });
    },

    // Get page text for a specific page number
    getPageText: (pageNumber: number): string | null => {
      const { pageContents } = get();
      return pageContents[pageNumber.toString()] || null;
    },

    // Get all page summaries
    getAllPageSummaries: (): Record<string, string> => {
      return get().pageSummaries;
    },

    // Get summary for a specific page
    getPageSummary: (pageNumber: number): string | null => {
      const { pageSummaries } = get();
      return pageSummaries[pageNumber.toString()] || null;
    },

    // ========================================================================
    // HIGHLIGHT MANAGEMENT (Following entityStore pattern)
    // ========================================================================

    /**
     * Internal helper: Apply local highlight upsert
     * Single code path for all highlight mutations
     */
    _applyLocalHighlightUpsert: (highlight: HighlightEntity, opts?: MutationOpts) => {
      const options: MutationOpts = {
        log: true,
        source: 'local',
        syncCRDT: true,
        ...opts
      };

      // Guard: CRDT updates must not sync back
      if (options.source === 'crdt' && options.syncCRDT !== false) {
        const errorMsg = `[pdfiumStore] 🔄 CRDT LOOP PREVENTED! CRDT updates must set syncCRDT=false. Highlight: ${highlight._id}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      const currentState = get();
      const spaceId = currentState.spaceId;

      // Real-time sync handled by entity-system Y.Doc (broadcast from REST handler).

      // Update local state
      set((state) => ({
        highlights: new Map(state.highlights).set(highlight._id, highlight)
      }));

      log.log('[pdfiumStore] Highlight upserted:', { 
        id: highlight._id, 
        source: options.source,
        syncCRDT: options.syncCRDT 
      });
    },

    /**
     * Internal helper: Apply local highlight delete
     * Single code path for all highlight deletions
     */
    _applyLocalHighlightDelete: (highlightId: string, opts?: MutationOpts) => {
      const options: MutationOpts = {
        log: true,
        source: 'local',
        syncCRDT: true,
        ...opts
      };

      // Guard: CRDT updates must not sync back
      if (options.source === 'crdt' && options.syncCRDT !== false) {
        const errorMsg = `[pdfiumStore] 🔄 CRDT LOOP PREVENTED! CRDT updates must set syncCRDT=false. Highlight: ${highlightId}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      const currentState = get();
      const spaceId = currentState.spaceId;

      // Real-time sync handled by entity-system Y.Doc (broadcast from REST handler).

      // Update local state
      set((state) => {
        const newHighlights = new Map(state.highlights);
        newHighlights.delete(highlightId);
        return { highlights: newHighlights };
      });

      log.log('[pdfiumStore] Highlight deleted:', { 
        id: highlightId,
        source: options.source,
        syncCRDT: options.syncCRDT 
      });
    },

    /**
     * Actions: Business logic for highlight operations
     */
    actions: {
      /**
       * Create a new highlight (optimistic + backend)
       */
      createHighlight: async (params: CreateHighlightParams) => {
        const { pageIndex, startIndex, endIndex, color, text, comment } = params;
        const currentState = get();
        const pdfId = currentState.entityId;

        if (!pdfId) {
          throw new Error('No PDF loaded');
        }

        // 1. Create temp highlight (optimistic)
        const tempId = `temp-highlight-${Date.now()}`;
        const tempHighlight: HighlightEntity = {
          _id: tempId,
          id: tempId,
          entityType: 'Highlight',
          type: 'Highlight',
          name: `Highlight on page ${pageIndex + 1}`,
          parentEntity: pdfId,
          skeleton: {
            '@type': 'Highlight',
            documentId: pdfId,
            pageIndex,
            startIndex,
            endIndex,
            color,
            opacity: 0.5,
            text,
            comment,
            tags: [],
            createdBy: 'current-user', // TODO: Get from auth
            createdAt: Date.now(),
            modifiedAt: Date.now()
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as HighlightEntity;

        // 2. Optimistic update (no CRDT, no undo)
        get()._applyLocalHighlightUpsert(tempHighlight, {
          log: false,
          syncCRDT: false
        });

        try {
          // 3. Backend create (strip temp ID and use create)
          const highlightData: any = {
            ...tempHighlight,
            _id: undefined, // Let backend assign ID
            id: undefined,
            spaceId: currentState.spaceId // Ensure spaceId is set
          };
          delete highlightData._id;
          delete highlightData.id;

          log.log('[pdfiumStore] Creating highlight entity:', { 
            pageIndex, 
            color, 
            textPreview: text.substring(0, 50),
            spaceId: highlightData.spaceId,
            parentEntity: highlightData.parentEntity
          });

          const created = await entityService.create(highlightData);
          
          log.log('[pdfiumStore] Highlight entity created on backend:', { 
            id: created._id,
            llmId: (created as any).llmId
          });

          // 4. Update PDF entity to add this highlight to skeleton.childEntities
          try {
            const pdfEntity = await entityService.get(pdfId);
            const currentSkeleton = (pdfEntity as any).skeleton || {};
            const currentChildren = currentSkeleton.childEntities || [];
            
            log.log('[pdfiumStore] PDF current skeleton.childEntities:', currentChildren);
            
            if (!currentChildren.includes(created._id)) {
              await entityService.update(pdfId, {
                skeleton: {
                  ...currentSkeleton,
                  childEntities: [...currentChildren, created._id]
                }
              } as any);
              
              log.log('[pdfiumStore] Added highlight to PDF skeleton.childEntities:', created._id);
            } else {
              log.log('[pdfiumStore] Highlight already in skeleton.childEntities');
            }
          } catch (updateError) {
            log.error('[pdfiumStore] Failed to update PDF skeleton.childEntities:', updateError);
            // Continue anyway - highlight is still created
          }

          // 5. Replace temp with real
          get()._applyLocalHighlightDelete(tempId, { log: false, syncCRDT: false });
          get()._applyLocalHighlightUpsert(created as HighlightEntity, {
            log: true,
            syncCRDT: true
          });

          log.log('[pdfiumStore] Highlight fully created and synced:', created._id);
          return created as HighlightEntity;
        } catch (error) {
          // 6. Rollback on error
          get()._applyLocalHighlightDelete(tempId, { log: false, syncCRDT: false });
          log.error('[pdfiumStore] Failed to create highlight - FULL ERROR:', error);
          console.error('[pdfiumStore] Highlight creation error stack:', error);
          throw error;
        }
      },

      /**
       * Update an existing highlight
       */
      updateHighlight: async (id: string, updates: Partial<HighlightEntity>) => {
        const currentState = get();
        const existing = currentState.highlights.get(id);

        if (!existing) {
          throw new Error(`Highlight ${id} not found`);
        }

        try {
          // Update backend
          const updated = await entityService.update(id, {
            ...updates,
            updated_at: new Date().toISOString()
          } as any);

          // Update local state
          get()._applyLocalHighlightUpsert(updated as HighlightEntity, {
            log: true,
            syncCRDT: true
          });

          log.log('[pdfiumStore] Highlight updated:', id);
        } catch (error) {
          log.error('[pdfiumStore] Failed to update highlight:', error);
          throw error;
        }
      },

      /**
       * Delete a highlight
       */
      deleteHighlight: async (id: string) => {
        const currentState = get();
        const highlight = currentState.highlights.get(id);
        const pdfId = currentState.entityId;

        try {
          // Delete from local state first (optimistic)
          get()._applyLocalHighlightDelete(id, {
            log: true,
            syncCRDT: true
          });

          // Delete from backend
          await fetchService(`${API_BASE_URL}/entities/${id}`, {
            method: 'DELETE',
            credentials: 'include'
          });

          // Update PDF entity to remove this highlight from skeleton.childEntities
          if (pdfId && highlight) {
            try {
              const pdfEntity = await entityService.get(pdfId);
              const currentSkeleton = (pdfEntity as any).skeleton || {};
              const currentChildren = currentSkeleton.childEntities || [];
              
              await entityService.update(pdfId, {
                skeleton: {
                  ...currentSkeleton,
                  childEntities: currentChildren.filter((childId: string) => childId !== id)
                }
              } as any);
              
              log.log('[pdfiumStore] Removed highlight from PDF skeleton.childEntities');
            } catch (updateError) {
              log.warn('[pdfiumStore] Failed to update PDF skeleton.childEntities:', updateError);
            }
          }

          log.log('[pdfiumStore] Highlight deleted:', id);
        } catch (error) {
          log.error('[pdfiumStore] Failed to delete highlight:', error);
          throw error;
        }
      },

      /**
       * Load all highlights for a PDF
       */
      loadHighlights: async (pdfId: string) => {
        try {
          log.log('[pdfiumStore] Loading highlights for PDF:', pdfId);
          
          // Get the PDF entity to check for child highlights
          const pdfEntity = await entityService.get(pdfId);
          const childHighlightIds = (pdfEntity as any).skeleton?.childEntities || [];
          
          log.log('[pdfiumStore] PDF entity skeleton.childEntities:', {
            childEntities: childHighlightIds,
            count: childHighlightIds.length,
            pdfId: pdfEntity._id
          });
          
          if (childHighlightIds.length === 0) {
            log.log('[pdfiumStore] No highlights found for PDF');
            set({ highlights: new Map() });
            return;
          }

          // Fetch each highlight entity
          const highlightPromises = childHighlightIds.map((id: string) =>
            entityService.get(id).catch((err: any) => {
              log.warn('[pdfiumStore] Failed to load highlight:', id, err);
              return null;
            })
          );

          const highlightResults = await Promise.all(highlightPromises);
          const highlights = highlightResults.filter((h): h is HighlightEntity => 
            h !== null && h.entityType === 'Highlight'
          );

          log.log('[pdfiumStore] Loaded highlights:', highlights.length);

          // Clear existing highlights
          set({ highlights: new Map() });

          // Add each highlight
          for (const highlight of highlights) {
            get()._applyLocalHighlightUpsert(highlight, {
              log: false,
              syncCRDT: false
            });
          }
        } catch (error) {
          log.error('[pdfiumStore] Failed to load highlights:', error);
          // Don't throw - just log and continue with empty highlights
          set({ highlights: new Map() });
        }
      }
    },

    /**
     * Get highlights for a specific page
     */
    getHighlightsForPage: (pageIndex: number) => {
      const highlights = Array.from(get().highlights.values());
      return highlights.filter(h => h.skeleton.pageIndex === pageIndex);
    },

    /**
     * Get all highlights
     */
    getAllHighlights: () => {
      return Array.from(get().highlights.values());
    }
  }));
};

// Create a map to store instances
const storeMap = new Map<string, ReturnType<typeof createPDFiumStore>>();

// Get or create store instance
export const getPDFiumStore = (windowId: string, spaceId: string) => {
  const key = `${windowId}-${spaceId}`;

  // Check if commands are already registered instead of using storeMap.size
  const { commands: registeredCommands } = useCommandRegistryStore.getState();
  if (!registeredCommands.has('pdf_getPage')) {
    registerCommands(commands);
    log.log('[PDFiumStore] Registered PDF commands');
  }

  if (!storeMap.has(key)) {
    const store = createPDFiumStore(windowId, spaceId);
    storeMap.set(key, store);
  }
  return storeMap.get(key)!;
};

// Hook to use the store
export const usePDFiumStore = (windowId: string, spaceId: string) => {
  return useStore(useMemo(() => getPDFiumStore(windowId, spaceId), [windowId, spaceId]));
};

// Cleanup function
export const cleanupPDFiumStore = (windowId: string, spaceId: string) => {
  const key = `${windowId}-${spaceId}`;
  const store = storeMap.get(key);
  if (store) {
    const state = store.getState();
    state.cleanup();
    storeMap.delete(key);
    log.log('[PDFiumStore] Cleaned up store for:', key);
  }
}; 